import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./MySubscriptionFilesView.styles";
import SubscriptionModel from "../../models/subscription/SubscriptionModel";
import * as SubscriptionAction from "../../stores/subscriptions/SubscriptionAction";
import { IRemoteDataTableColumn } from "../components/RemoteDataTable/interfaces";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useAuthContext } from "../../auth/AuthProvider";
import { useParams } from "react-router-dom";
import { Breadcrumbs } from "../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../components/CenteredContainer/CenteredContainer";
import TableRemoteData, { getParamsFromQuery } from "../components/RemoteDataTable/RemoteDataTable";
import ToastStatusEnum from "../../constants/ToastStatusEnum";
import { requestUserSubscriptionFiles } from "../components/RemoteDataTable/requests";

export interface IMySubscriptionFilesViewProps extends WithStyles<typeof styles> {
}

const MySubscriptionFilesViewPure: React.FunctionComponent<IMySubscriptionFilesViewProps> = ({
                                                                                                 classes
                                                                                             }: IMySubscriptionFilesViewProps) => {
    const authContext = useAuthContext();
    const userId = authContext?.user?.id;
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {subscriptionId} = useParams();
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        dispatch(SubscriptionAction.requestUserSubscriptions(userId));
    }, [userId, subscriptionId, dispatch]);

    const userSubscriptionsFromState = useSelector(state => state.subscriptions?.userSubscriptions) || {};
    const currentSubscription = Object.values(userSubscriptionsFromState)?.find(
        (s: SubscriptionModel) => s.id === parseInt(subscriptionId)
    ) as any;
    const subscriptionTitle = currentSubscription?.name || "";

    const columns: IRemoteDataTableColumn[] = [
        {
            type: "text",
            title: t("general.filename"),
            field: "filename"
        },
        {
            type: "size",
            title: t("general.size"),
            field: "size"
        },
        {
            type: "date",
            title: t("general.date"),
            field: "date"
        },
        {
            type: "dateTime",
            title: t("general.updatedOn"),
            field: "updated"
        },
        {
            title: t("general.download"),
            type: "link",
            sortDisable: true,
            linkField: "link",
            linkTitle: t("general.download")
        }
    ];

    const handleData = query => {
        return new Promise((resolve, reject) => {
        const params = getParamsFromQuery(query);

        requestUserSubscriptionFiles(userId, subscriptionId, params)
            .then(response => {
                resolve({
                    data: response.data?.content,
                    page: query.page,
                    totalCount: response.data?.totalCount
                });
            })
            .catch(error => {
                enqueueSnackbar("List of subscription files cannot be loaded: " + error, {
                    variant: ToastStatusEnum.Error
                })
                reject(error);
            })
      })
    }

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    {title: t("manageAccount.myAccount"), href: "/my-account"},
                    {title: t("manageAccount.mySubscriptions.mySubscriptions"), href: "/my-account/my-subscriptions"},
                    {title: `${t("general.files")} - ${subscriptionTitle}`}
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h3">{t("manageAccount.mySubscriptions.mySubscriptions")}</Typography>
            </div>
            <TableRemoteData columns={columns} onQueryChange={handleData}/>
        </CenteredContainer>
    );
};

export const MySubscriptionFilesView = withStyles(styles)(MySubscriptionFilesViewPure);
